import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { Sync, Check, Warning, Api } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { piSyncApi, PIJobSyncResults } from '../services/api';
import api from '../services/api';
import PIJobSyncDialog from '../components/PIJobSyncDialog';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [piIntegrationStatus, setPiIntegrationStatus] = useState({
    enabled: false,
    configured: false,
    lastSync: null as string | null,
  });
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);

  // Check if user has admin access
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchIntegrationStatus();
  }, []);

  const fetchIntegrationStatus = async () => {
    try {
      setLoading(true);
      // Check PI integration status via health endpoint
      const response = await api.get('/health');
      
      // In production, these would be checked server-side
      // For now, we'll assume it's configured if the integration endpoints work
      setPiIntegrationStatus({
        enabled: true, // Will be determined by server response
        configured: true, // Will be determined by server response
        lastSync: null,
      });
    } catch (error) {
      console.error('Error fetching integration status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncJobs = () => {
    setMessage(null);
    setSyncDialogOpen(true);
  };

  const handleSyncComplete = (results: PIJobSyncResults) => {
    const jobParts = [];
    const { jobs, candidates } = results;

    if (jobs.linked > 0) jobParts.push(`${jobs.linked} linked`);
    if (jobs.created > 0) jobParts.push(`${jobs.created} created`);
    if (jobs.updated > 0) jobParts.push(`${jobs.updated} updated`);
    if (jobs.unlinked > 0) jobParts.push(`${jobs.unlinked} unlinked`);

    const messageParts = [];
    if (jobParts.length > 0) {
      messageParts.push(`Jobs: ${jobParts.join(', ')}`);
    }
    if (candidates.imported > 0) {
      messageParts.push(`Candidates: ${candidates.imported} imported`);
    }

    if (messageParts.length > 0) {
      setMessage({
        text: `Import complete! ${messageParts.join(' | ')}`,
        severity: 'success'
      });
    } else {
      setMessage({
        text: 'No changes were made',
        severity: 'info'
      });
    }

    if (jobs.errors.length > 0 || candidates.errors.length > 0) {
      console.error('Import errors:', { jobErrors: jobs.errors, candidateErrors: candidates.errors });
    }
  };

  const handleTestConnection = async () => {
    try {
      setIsTesting(true);
      setMessage(null);
      
      const response = await api.get('/pi-sync/test');
      
      if (response.data.success) {
        setMessage({
          text: 'PI API connection successful!',
          severity: 'success'
        });
        setPiIntegrationStatus(prev => ({ ...prev, configured: true }));
      }
    } catch (error: any) {
      console.error('PI API test error:', error.response?.data);
      setMessage({
        text: error.response?.data?.error || 'Failed to connect to PI API',
        severity: 'error'
      });
      
      // If admin, show debug endpoint
      if (isAdmin && error.response?.data?.details) {
        console.log('Error details:', error.response.data.details);
      }
    } finally {
      setIsTesting(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">
          You need administrator access to view settings.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        System Settings
      </Typography>

      {/* PI Integration Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Predictive Index Integration
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body2" color="text.secondary">
              Status:
            </Typography>
            {piIntegrationStatus.configured ? (
              <Chip
                icon={<Check />}
                label="Configured"
                color="success"
                size="small"
              />
            ) : (
              <Chip
                icon={<Warning />}
                label="Not Configured"
                color="warning"
                size="small"
              />
            )}
          </Box>

          {piIntegrationStatus.configured && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Webhook URL:</strong> {window.location.origin}/api/pi-sync/webhook
              </Typography>
              <Typography variant="caption" display="block" mt={1}>
                Add this URL to your PI webhook configuration to receive real-time assessment updates.
              </Typography>
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Connection & Sync Operations
        </Typography>
        
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Api />}
            onClick={handleTestConnection}
            disabled={isTesting}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<Sync />}
            onClick={handleSyncJobs}
            disabled={!piIntegrationStatus.configured}
          >
            Import from PI
          </Button>
        </Box>
        
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Test your PI API connection or import jobs and candidates from your Predictive Index account.
        </Typography>

        {message && (
          <Alert severity={message.severity} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </Paper>

      {/* Other Settings Sections */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Email settings are configured via environment variables.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          System Information
        </Typography>
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Typography variant="body2">
            <strong>Environment:</strong> {process.env.NODE_ENV}
          </Typography>
          <Typography variant="body2">
            <strong>Version:</strong> 1.0.0
          </Typography>
        </Box>
      </Paper>

      {/* PI Job Sync Dialog */}
      <PIJobSyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        onComplete={handleSyncComplete}
      />
    </Container>
  );
};

export default Settings;